'use client'
import { CreateUserBody, UpdateUserBody, User } from '@/types/user'
import { fetchClient } from '@/services/api/client'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { env } from '@/env'
import { useToast } from '@/hooks/use-toast'
import {
  deleteCookie,
  getCookie,
  OptionsType,
  setCookie,
} from 'cookies-next/client'

export interface AuthUserSignUp {
  username: string
  name_en: string
  surname_en: string
  password?: string
  google_id?: string
  email: string | null
  emailVerificationToken?: string
}

interface AuthCheckPayload {
  username: string
  email?: string
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  error: string | null
  loading: boolean
}

interface AuthProvider {
  user: User | null
  error: string | null
  loading: boolean
  signIn: (
    username: string,
    password: string,
    turnstileToken: string
  ) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  signUp: (
    user: CreateUserBody,
    turnstileToken: string,
    noRefresh?: boolean
  ) => Promise<boolean>
  isAuthenticated: () => boolean
  getUser: () => Promise<User | null>
  updateUser: (user: UpdateUserBody) => Promise<boolean>
  saveTokens: (accessToken: string | null, refreshToken: string | null) => void
  refreshAuth: (accessToken?: string) => Promise<void>
  check: (payload: AuthCheckPayload) => Promise<boolean>
}

const AuthContext = createContext<AuthProvider>({
  user: null,
  error: null,
  loading: false,
  signIn: async () => {
    throw new Error('signIn function not implemented')
  },
  signInWithGoogle: async () => {
    throw new Error('signInWithGoogle function not implemented')
  },
  signOut: async () => {
    throw new Error('signOut function not implemented')
  },
  signUp: async () => {
    throw new Error('signUp function not implemented')
  },
  isAuthenticated: () => {
    throw new Error('isAuthenticated function not implemented')
  },
  getUser: async () => {
    throw new Error('getUser function not implemented')
  },
  updateUser: async () => {
    throw new Error('updateUser function not implemented')
  },
  saveTokens: () => {
    throw new Error('saveTokens function not implemented')
  },
  refreshAuth: async () => {
    throw new Error('refreshAuth function not implemented')
  },
  check: async () => {
    throw new Error('check function not implemented')
  },
})

const COOKIE_EXPIRY = 60 * 60 * 24 * 30 // 30 days
const options: OptionsType = {
  maxAge: COOKIE_EXPIRY,
  expires: new Date(Date.now() + COOKIE_EXPIRY),
  path: '/',
  // domain: env.NEXT_PUBLIC_FRONTEND_URL.startsWith('http://localhost')
  //   ? 'localhost'
  //   : '.contester.life', // Share across www and root
  secure: true, // Required if your site is HTTPS (which it should be)
  sameSite: 'lax', // Safe default; use 'none' if cross-site is needed
}

const sharedCookieOptions: OptionsType = {
  path: '/',
  // domain: env.NEXT_PUBLIC_FRONTEND_URL.startsWith('http://localhost')
  //   ? 'localhost'
  //   : '.contester.life',
}

export const clearAuthCookies = () => {
  deleteCookie('access_token', sharedCookieOptions)
  deleteCookie('refresh_token', sharedCookieOptions)
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    error: null,
    loading: true,
  })
  const router = useRouter()
  const { toast } = useToast()
  const params = useSearchParams()

  const checkCookieVersion = useCallback(() => {
    const cookieVersion = getCookie('cookie_version')
    if (cookieVersion !== env.NEXT_PUBLIC_COOKIE_VERSION) {
      // Clear cookies if version does not match
      deleteCookie('access_token', sharedCookieOptions)
      deleteCookie('refresh_token', sharedCookieOptions)
      setCookie('cookie_version', env.NEXT_PUBLIC_COOKIE_VERSION, options)
    }

    return cookieVersion === env.NEXT_PUBLIC_COOKIE_VERSION
  }, [])

  const getAuthCookies = useCallback(() => {
    checkCookieVersion()

    const accessToken = getCookie('access_token')
    const refreshToken = getCookie('refresh_token')

    return {
      accessToken: accessToken ? String(accessToken) : null,
      refreshToken: refreshToken ? String(refreshToken) : null,
    }
  }, [checkCookieVersion])

  const saveTokens = useCallback(
    (accessToken: string | null, refreshToken: string | null) => {
      checkCookieVersion()

      if (accessToken !== '' && accessToken !== null)
        setCookie('access_token', accessToken, options)

      if (refreshToken !== '' && refreshToken !== null)
        setCookie('refresh_token', refreshToken, options)
    },
    [checkCookieVersion]
  )

  const refreshAuth = useCallback(
    async (newAccessToken?: string) => {
      setLoadingState(true)
      const { accessToken, refreshToken } = getAuthCookies()

      const isValid = checkCookieVersion()
      if (!isValid) return

      await fetchClient
        .GET('/auth/me', {
          headers: {
            ...(accessToken || newAccessToken
              ? {
                  Authorization: `Bearer ${newAccessToken ? newAccessToken : accessToken}`,
                }
              : {}),
          },
        })
        .then(async (res) => {
          const response = res.response

          if (response.ok) {
            setAuthState((authState) => ({
              ...authState,
              user: res.data?.user || null,
              isAuthenticated: true,
              error: null,
              loading: false,
            }))

            return
          }

          if (refreshToken) {
            const res = await fetchClient.POST('/auth/refresh', {
              body: {
                refreshToken,
              },
            })
            const response = res.response
            if (response.ok) {
              saveTokens(
                res.data?.accessToken || '',
                res.data?.refreshToken || ''
              )
            } else {
              setAuthState((authState) => ({
                ...authState,
                user: null,
                isAuthenticated: false,
                error: 'Invalid Credentials',
                loading: false,
              }))
              deleteCookie('access_token')
              deleteCookie('refresh_token')
            }
          } else {
            setAuthState((authState) => ({
              ...authState,
              user: null,
              isAuthenticated: false,
            }))
          }
        })
        .catch((err) => {
          setAuthState((authState) => ({
            ...authState,
            user: null,
            isAuthenticated: false,
            error: err.message,
            loading: false,
          }))
        })

      setLoadingState(false)
    },
    [setAuthState, getAuthCookies, saveTokens, checkCookieVersion]
  )

  useEffect(() => {
    refreshAuth()
  }, [refreshAuth])

  async function signIn(
    username: string,
    password: string,
    turnstileToken: string
  ) {
    setLoadingState(true)

    await fetchClient
      .POST('/auth/signin', {
        body: {
          username,
          password,
          turnstileToken,
        },
      })
      .then((res) => {
        const response = res.response

        if (!response.ok) {
          throw new Error('Login failed')
        }

        setAuthState((authState) => ({
          ...authState,
        }))

        saveTokens(res.data?.accessToken || '', res.data?.refreshToken || '')

        toast({
          title: 'เข้าสู่ระบบสำเร็จ',
          description: 'ยินดีต้อนรับเข้าสู่ Contester.life',
        })
        return res
      })
      .then(() => {
        refreshAuth().then(() => {
          const navigateTo = params.get('navigateTo')
          // using router.push does not navigate in staging/production
          // https://github.com/vercel/next.js/discussions/51782#discussioncomment-12657879
          // TODO: find a way to navigate using router that works in staging/production
          window.location.href = navigateTo ? navigateTo : '/'
        })
      })
      .catch((err) => {
        toast({
          variant: 'destructive',
          title: 'เข้าสู่ระบบไม่สำเร็จ',
          description: 'โปรดตรวจสอบชื่อผู้ใช้งานและรหัสผ่าน',
        })

        setAuthState((authState) => ({
          ...authState,
          error: err.message,
        }))
      })
      .finally(() => {
        setLoadingState(false)
      })

    return
  }

  async function signInWithGoogle() {
    // redirect to google auth
    const navigateTo = params.get('navigateTo')
    router.push(
      `${env.NEXT_PUBLIC_BACKEND_URL}/auth/google${navigateTo ? `?${new URLSearchParams({ navigateTo }).toString()}` : ''}`
    )
  }

  async function signOut() {
    const { accessToken } = getAuthCookies()
    setLoadingState(true)
    const res = await fetchClient.POST('/auth/signout', {
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    })
    setLoadingState(false)

    deleteCookie('access_token')
    deleteCookie('refresh_token')

    setAuthState((authState) => ({
      ...authState,
      isAuthenticated: false,
      user: null,
    }))

    if (res.response.status !== 200) {
      setAuthState((authState) => ({
        ...authState,
        error: 'Invalid Credentials',
      }))
      return
    }

    toast({
      title: 'ออกจากระบบสำเร็จ',
      description: 'คุณได้ออกจากระบบแล้ว',
    })

    router.push('/')
    return
  }

  function setLoadingState(loadingState: boolean) {
    setAuthState((authState) => ({
      ...authState,
      loading: loadingState,
    }))
  }

  async function signUp(
    user: CreateUserBody,
    turnstileToken: string,
    updateLoading: boolean = true
  ) {
    if (updateLoading) {
      setLoadingState(true)
    }
    const res = await fetchClient.POST('/user', {
      body: {
        ...user,
        name_en: user.name_en,
        surname_en: user.surname_en,
        google_id: user.google_id,
        username: user.username,
        password: user.password,
        email: user.email,
        email_verification_token: user.email_verification_token,
        turnstileToken: turnstileToken,
      },
    })

    // Maybe move set loading state to bottom most in case that the router push take long time?
    setLoadingState(false)

    if (res.response.ok) {
      await refreshAuth(res.data?.data.accessToken)

      saveTokens(
        res.data?.data.accessToken || null,
        res.data?.data.refreshToken || null
      )

      toast({
        title: 'สร้างผู้ใช้งานสำเร็จ',
        description: 'ยินดีต้อนรับเข้าสู่ Contester.life',
      })

      if (res.data?.data.navigateTo) {
        router.push(res.data?.data.navigateTo)
      }
      return true
    } else if (res.response.status === 409) {
      toast({
        variant: 'destructive',
        title: 'เกิดข้อผิดพลาด',
        description: 'ชื่อผู้ใช้งานนี้มีอยู่แล้ว',
      })
      setAuthState((authState) => ({
        ...authState,
        error: 'Username already exists',
      }))
    } else {
      toast({
        variant: 'destructive',
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถสร้างผู้ใช้งานได้',
      })

      setAuthState((authState) => ({
        ...authState,
        error: 'Invalid Credentials',
      }))
    }

    await refreshAuth()

    return false
  }

  async function updateUser(user: Partial<User>) {
    const { accessToken } = getAuthCookies()
    setLoadingState(true)
    const res = await fetchClient.PUT('/user/{id}', {
      params: {
        path: {
          id: String(authState.user?.id),
        },
      },
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: user,
    })
    setLoadingState(false)

    const response = res.response
    if (response.status !== 200) {
      toast({
        variant: 'destructive',
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัปเดตข้อมูลผู้ใช้งานได้',
      })
      setAuthState((authState) => ({
        ...authState,
        error: 'Invalid Credentials',
      }))

      return false
    }

    await refreshAuth() // Ensure state updates with new user info
    toast({
      title: 'สร้างผู้ใช้แล้ว',
      description: 'ยินดีต้อนรับเข้าสู่ Contester.life',
    })
    const navigateTo = params.get('navigateTo')
    router.push(
      `/register/success${navigateTo ? `?navigateTo=${navigateTo}` : ''}`
    )

    return true
  }

  async function check(payload: AuthCheckPayload) {
    setLoadingState(true)
    const res = await fetchClient.POST('/auth/check', {
      body: payload,
    })

    setLoadingState(false)

    if (res.response.ok) {
      return true
    }

    return false
  }

  const isAuthenticated = useCallback(() => {
    return authState.isAuthenticated
  }, [authState.isAuthenticated])

  async function getUser() {
    return authState.user
  }

  if (authState.loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-b-2 border-neutral-500"></div>
      </div>
    )
  }

  return (
    <AuthContext.Provider
      value={{
        user: authState.user,
        error: authState.error,
        loading: authState.loading,
        signIn,
        signInWithGoogle,
        signOut,
        signUp,
        isAuthenticated,
        getUser,
        updateUser,
        saveTokens,
        refreshAuth,
        check,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
